export { stateHasWasteCodes } from "./stateWasteCodes";

/**
 * Waste tag a vendor import puts on a pickup that rode on the hauler's route
 * e-Manifest. A tagged pickup gets no missing-manifest discrepancy.
 */
export const CONSOLIDATED_WASTE_TAG_NAME = "Consolidated";

/** `vendorSource` of World Oil vendor manifests, the only source whose Consolidated tag is trusted. */
export const WORLD_OIL_VENDOR_SOURCE = "World Oil";

/** Cap on a blocked manifest's note, enforced in the dialog and the mutation resolver. */
export const MAX_BLOCKED_NOTE_LENGTH = 2000;

// Exactly Postgres `btrim(x, E' \t\n\r\f\v')`, which is what the waste-code SQL
// in encamp-waste-data-access applies. Deliberately narrower than
// String.trim(): that also strips NBSP and BOM, and a code padded with one of
// those would then normalize differently on the two sides.
const SQL_TRIM_SET_PATTERN = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;

/**
 * Trims a waste code the way the SQL that compares codes trims it. Every
 * TypeScript path that has to agree with that SQL uses this rather than
 * `String.trim()`, so the two cannot disagree on a padded code.
 */
export function sqlBtrim(value: string): string {
  return value.replace(SQL_TRIM_SET_PATTERN, "");
}

export const STATES_NOT_FOLLOWING_FEDERAL_GENERATOR_STATUS = [
  "CA",
  "KS",
  "ME",
  "MD",
  "MA",
  "MN",
  "RI",
  "TX",
  "WA",
  "DC",
];
const statesNotFollowingFederalGeneratorStatus = new Set(
  STATES_NOT_FOLLOWING_FEDERAL_GENERATOR_STATUS
);

export const stateDoesNotFollowFederalGeneratorStatus = (
  state: string | null | undefined
): boolean => {
  if (!state) return false;
  return statesNotFollowingFederalGeneratorStatus.has(state.toUpperCase());
};

/**
 * States that do not require OI (Off-site Installation) forms in RCRA flat-file exports.
 * These states do not require destination address or transporter address data for reporting.
 */
export const STATES_WITHOUT_OI_FORMS = ["KS", "MD", "MI", "NC", "OK", "PA", "WI"];
const statesWithoutOiForms = new Set(STATES_WITHOUT_OI_FORMS);

export const stateRequiresOiForms = (state: string | null | undefined): boolean => {
  if (!state) return true; // Default to requiring OI forms when state is unknown
  return !statesWithoutOiForms.has(state.toUpperCase());
};

/**
 * States that require generators to register each new waste stream with
 * the state. The state assigns an identifier (e.g. TN `WS-1`, `WS-2`)
 * which is stored on `StateWasteStreamRegistration.stateRegisteredId`.
 */
export const WASTE_STREAM_REGISTRATION_REQUIRED_STATES = ["TN", "SC", "KY", "MN", "TX"] as const;
export type WasteStreamRegistrationRequiredState =
  (typeof WASTE_STREAM_REGISTRATION_REQUIRED_STATES)[number];

const wasteStreamRegistrationRequiredStates = new Set<string>(
  WASTE_STREAM_REGISTRATION_REQUIRED_STATES
);

export const stateRequiresWasteStreamRegistration = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return wasteStreamRegistrationRequiredStates.has(state.toUpperCase());
};

// Some states' registries omit dimensions the registration matcher would
// otherwise score on. For those, the matcher drops the missing dimension(s)
// and redistributes their weight across the ones the state actually
// publishes, so a stream that matches on the available signals isn't capped
// by neutral scores on data the state can never populate. Kentucky's registry
// publishes neither a DOT/UN number nor a form code. Texas's registry
// publishes form codes but no DOT/UN number.
export const REGISTRATION_STATES_WITHOUT_DOT: readonly string[] = ["KY", "TX"];

export const REGISTRATION_STATES_WITHOUT_FORM: readonly string[] = ["KY"];

export const registrationStateOmitsDot = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return REGISTRATION_STATES_WITHOUT_DOT.includes(state.toUpperCase());
};

export const registrationStateOmitsForm = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return REGISTRATION_STATES_WITHOUT_FORM.includes(state.toUpperCase());
};

/**
 * States where the registration's state-assigned id is itself a state waste
 * code that rides the stream's manifests — so a stream carrying that id among
 * its `stateWasteCodes` is that registration's stream. Texas is the first: its
 * 8-char TCEQ waste code both registers the stream and appears on its lines.
 * Contrast TN/KY, whose ids are opaque sequences (e.g. `WS-1`) that never
 * appear on a stream. Used by the registration matcher to score and to gate
 * linkability on a state-code match.
 */
export const REGISTRATION_ID_IS_STATE_WASTE_CODE_STATES = ["TX"] as const;
const registrationIdIsStateWasteCodeStates = new Set<string>(
  REGISTRATION_ID_IS_STATE_WASTE_CODE_STATES
);

export const registrationIdIsStateWasteCode = (state: string | null | undefined): boolean =>
  !!state && registrationIdIsStateWasteCodeStates.has(state.toUpperCase());
